// REACT IMPORTS
import React, { Fragment, useEffect, useRef } from "react";

// LIBRARY IMPORTS
import { Dialog, Transition } from "@headlessui/react";

/**
 * Side sheet built on the Headless UI Dialog.
 *
 * Replaces `react-modern-drawer`, which emitted a single accessibility
 * attribute — `role="navigation"` — and had no focus trap, no `aria-modal`, no
 * Escape handling and no focus restore. Opening a preview left keyboard focus on
 * the page underneath, so Tab walked content the drawer was covering and there
 * was no way out. Call sites also passed `overlayClassName="pointer-events-none"`,
 * which disabled click-outside dismissal on top of that.
 *
 * The prop surface deliberately mirrors `react-modern-drawer` so the remaining
 * call sites can migrate by changing their import.
 */
interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  direction?: "left" | "right";
  /** CSS width for the panel, e.g. "590px" or "100vw". */
  size?: number | string;
  enableOverlay?: boolean;
  overlayColor?: string;
  overlayOpacity?: number;
  overlayClassName?: string;
  className?: string;
  /** Accepted for API compatibility — the Dialog always locks page scroll. */
  lockBackgroundScroll?: boolean;
  /** Set false for a blocking drawer that Escape and the backdrop cannot close. */
  dismissable?: boolean;
  /** Stacking order, for drawers that must sit above another layer. */
  zIndex?: number;
  "data-testid"?: string;
  "aria-label"?: string;
}

const SideDrawer = (props: SideDrawerProps) => {
  const {
    open,
    onClose,
    children,
    direction = "right",
    size = "590px",
    enableOverlay = true,
    overlayColor = "#000",
    overlayOpacity = 0.4,
    overlayClassName = "",
    className = "",
    dismissable = true,
    zIndex = 999,
    ...drawerProps
  } = props;

  const panelRef = useRef<HTMLDivElement | null>(null);

  // Drawers opened from a Headless UI Menu (the kebab menus on the dashboard
  // and subscriptions lists) lose their initial focus: the Menu restores focus
  // to its trigger button when its leave transition finishes, which lands after
  // the Dialog has already focused the panel. One rAF is too early to win that
  // race, so focus is re-asserted once more after the transition — bounded, and
  // only while focus is actually outside the panel.
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const focusPanel = () => {
      const panel = panelRef.current;
      if (!cancelled && panel && !panel.contains(document.activeElement)) {
        panel.focus();
      }
    };
    const raf = requestAnimationFrame(focusPanel);
    const settle = setTimeout(focusPanel, 250);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, [open]);

  const isLeft = direction === "left";
  const offscreen = isLeft ? "-translate-x-full" : "translate-x-full";

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex }}
        // Headless UI routes both Escape and a backdrop click here.
        onClose={dismissable ? onClose : () => {}}
        initialFocus={panelRef}
      >
        {enableOverlay && (
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={`fixed inset-0 ${overlayClassName}`}
              style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}
              aria-hidden="true"
            />
          </Transition.Child>
        )}

        <div className="fixed inset-0 overflow-hidden">
          <div
            className={`fixed inset-y-0 ${
              isLeft ? "left-0" : "right-0"
            } flex max-w-full`}
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom={offscreen}
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-200"
              leaveFrom="translate-x-0"
              leaveTo={offscreen}
            >
              {/* No overflow rule here: the drawer contents manage their own
                  scrolling, matching how react-modern-drawer's container
                  behaved. */}
              <Dialog.Panel
                ref={panelRef}
                tabIndex={-1}
                // transform-gpu keeps a transform on the panel at all times, which makes
                // it the containing block for `position: fixed` children.
                // react-modern-drawer did this implicitly; drawer content that
                // pins a header with `fixed top-0 w-full` (the add/update card,
                // bank and wallet forms) sizes against the viewport without it,
                // pushing the close button off-screen. It reads the same
                // --tw-translate-x the slide transition animates, so the
                // animation is unaffected.
                className={`h-full transform-gpu bg-white shadow-[0_0_10px_5px_rgba(0,0,0,0.1)] outline-none ${className}`}
                style={{ width: size, maxWidth: "100%" }}
                data-testid={drawerProps["data-testid"]}
                aria-label={drawerProps["aria-label"]}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SideDrawer;
